/**
 * KRA Photo Journal – Tag System Helpers
 *
 * 4 Airtable Multi-Select tag groups:
 *   kimono_tag       → Trang phục (Outfit / Kimono type)
 *   shoot_tag        → Kiểu chụp (Shoot type)
 *   season_tag       → Mùa (Season)
 *   scene_tag        → Cảnh (Scene / Location)
 *   kimono_color_tag → Màu sắc (Kimono color)
 *
 * Filter logic:
 *   - AND between groups (photo must match ALL groups that have active filters)
 *   - OR within a group (photo needs at least ONE matching tag in that group)
 */

import type { PhotoRecord } from "@kra/photo-journal-shared";
import { TAG_GROUPS, type TagGroup, type TagGroupKey } from "@kra/photo-journal-shared";

export { TAG_GROUPS };
export type { TagGroup, TagGroupKey };

/**
 * TagFilters maps each group key to the selected filter values for that group.
 * An empty array means "no filter" for that group.
 */
export type TagFilters = Partial<Record<TagGroupKey, string[]>>;

/**
 * Returns true if the photo matches the active tag filters.
 * Logic: AND between groups, OR within group.
 *
 * Example:
 *   { kimonoTags: ["Furisode", "Yukata"], shootTags: ["Couple"] }
 *   → photo must have (Furisode OR Yukata) AND (Couple)
 */
export function matchesTagFilters(photo: PhotoRecord, filters: TagFilters): boolean {
  for (const group of TAG_GROUPS) {
    const selectedValues = filters[group.key];
    if (!selectedValues || selectedValues.length === 0) continue; // no filter for this group

    const photoValues: string[] = photo[group.key] ?? [];
    // OR within group: at least one selected value must be present in the photo's tags
    const hasMatch = selectedValues.some((v) => photoValues.includes(v));
    if (!hasMatch) return false;
  }
  return true;
}

/**
 * Collect all unique tag values across all photos for a given group key.
 * Used to populate the filter panel options.
 */
export function collectTagOptions(photos: PhotoRecord[], key: TagGroupKey): string[] {
  const set = new Set<string>();
  for (const p of photos) {
    const tags: string[] = p[key] ?? [];
    for (const t of tags) {
      if (t.trim()) set.add(t.trim());
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, "vi"));
}

/**
 * Returns total number of active tag filter selections across all groups.
 */
export function countActiveTagFilters(filters: TagFilters): number {
  return Object.values(filters).reduce((sum, arr) => sum + (arr?.length ?? 0), 0);
}

/**
 * Flattens all tags from a photo into a single array for display.
 */
export function getAllTags(photo: PhotoRecord): Array<{ value: string; group: TagGroup }> {
  const result: Array<{ value: string; group: TagGroup }> = [];
  for (const group of TAG_GROUPS) {
    const tags: string[] = photo[group.key] ?? [];
    for (const value of tags) {
      result.push({ value, group });
    }
  }
  return result;
}
